import React, { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Navigation, MapPin, Clock, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

// Customer-side live tracking panel. Polls GET /jobs/:id/trip every 20s while
// mounted. All displayed coordinates come straight from the server already
// grid-rounded to ~200m (`_grid_round` in backend). No raw location is ever
// shown. Rendered inside each upcoming booking card in MyBookings.
const STATUS_LABEL = {
  not_started: { text: "Not started", tone: "slate", icon: Clock },
  on_the_way:  { text: "Cleaner is on the way", tone: "teal", icon: Navigation },
  arriving_soon: { text: "Arriving soon", tone: "emerald", icon: Navigation },
  arrived: { text: "Arrived", tone: "emerald", icon: CheckCircle2 },
  cancelled: { text: "Cancelled", tone: "red", icon: AlertTriangle },
  location_unavailable: { text: "Live location unavailable — ETA may be unavailable", tone: "amber", icon: AlertTriangle },
};

const TONE = {
  slate:   "border-slate-200 bg-slate-50 text-slate-700",
  teal:    "border-teal-200 bg-teal-50 text-teal-800",
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
  amber:   "border-amber-200 bg-amber-50 text-amber-800",
  red:     "border-red-200 bg-red-50 text-red-800",
};

export default function TripTracker({ jobId }) {
  const [trip, setTrip] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/jobs/${jobId}/trip`);
      setTrip(data);
    } catch (_) { /* 403/404 silently hides the panel */ }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // Poll every 20s only when the panel is open AND the trip could change.
    if (open) {
      timerRef.current = setInterval(load, 20000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!trip) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs font-mono uppercase tracking-widest text-teal-600 hover:text-teal-700 inline-flex items-center gap-1"
        data-testid={`booking-track-${jobId}`}
      >
        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Navigation className="w-3 h-3" />}
        Track cleaner
      </button>
    );
  }

  const meta = STATUS_LABEL[trip.status] || STATUS_LABEL.not_started;
  const Icon = meta.icon;
  const loc = trip.cleaner_location;
  const haveLoc = !!(loc && loc.lat != null && loc.lng != null);
  // OSM embed URL — zoom around the point with ~0.02° bbox (~2km).
  const osmSrc = haveLoc
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${loc.lng - 0.02}%2C${loc.lat - 0.015}%2C${loc.lng + 0.02}%2C${loc.lat + 0.015}&layer=mapnik&marker=${loc.lat}%2C${loc.lng}`
    : null;

  return (
    <Card className="mt-3 border-slate-200 overflow-hidden w-full md:min-w-[300px]" data-testid={`trip-tracker-${jobId}`}>
      <div className="p-3 space-y-2">
        {/* Status pill */}
        <div className={`flex items-center gap-2 rounded-md border px-2 py-1 text-[11px] font-semibold ${TONE[meta.tone]}`} data-testid={`trip-status-${jobId}`}>
          <Icon className="w-3.5 h-3.5" /> {meta.text}
        </div>

        {/* Cleaner identity */}
        {trip.cleaner_name && (
          <div className="flex items-center gap-2" data-testid={`trip-cleaner-${jobId}`}>
            {trip.cleaner_photo ? (
              <img src={trip.cleaner_photo} alt="" className="w-7 h-7 rounded-full object-cover border border-slate-200" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-slate-200" />
            )}
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">{trip.cleaner_name}</div>
              {trip.transport_type && (
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono" data-testid={`trip-transport-${jobId}`}>
                  via {trip.transport_type.replace(/_/g, " ")}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ETA + distance */}
        {(trip.cleaner_eta_minutes != null || trip.cleaner_distance_km != null) && (
          <div className="grid grid-cols-2 gap-2 text-xs">
            {trip.cleaner_eta_minutes != null && (
              <div className="rounded border border-slate-200 p-2" data-testid={`trip-eta-${jobId}`}>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">ETA</div>
                <div className="text-slate-900 font-semibold">~{trip.cleaner_eta_minutes} min</div>
              </div>
            )}
            {trip.cleaner_distance_km != null && (
              <div className="rounded border border-slate-200 p-2" data-testid={`trip-distance-${jobId}`}>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Distance</div>
                <div className="text-slate-900 font-semibold">{trip.cleaner_distance_km} km</div>
              </div>
            )}
          </div>
        )}

        {/* Last update */}
        {trip.last_update && (
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500" data-testid={`trip-last-update-${jobId}`}>
            Updated {new Date(trip.last_update).toLocaleTimeString()}
          </div>
        )}

        {/* Map */}
        {haveLoc ? (
          <div className="rounded border border-slate-200 overflow-hidden" data-testid={`trip-map-${jobId}`}>
            <iframe
              key={trip.last_update}
              title="Cleaner approximate location"
              src={osmSrc}
              className="w-full"
              style={{ height: 180, border: 0 }}
              loading="lazy"
            />
            <div className="text-[10px] text-slate-500 px-2 py-1 bg-slate-50 flex items-center gap-1">
              <MapPin className="w-3 h-3" />Approximate location · rounded to protect privacy
            </div>
          </div>
        ) : (
          <div className="text-xs text-slate-500 rounded border border-dashed border-slate-200 p-3 flex items-center gap-2" data-testid={`trip-no-map-${jobId}`}>
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
            {trip.booking_status === "cancelled" ? "This booking was cancelled." :
              trip.trip_active ? "Waiting for the first location update…" :
                "Cleaner hasn't started the trip yet."}
          </div>
        )}
      </div>
    </Card>
  );
}
